import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';

const PILL_RADIUS = 'var(--border-radius-lg)';
const GAP_HALF = 'var(--gap-half)';

export function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const r0 = el.getBoundingClientRect?.();
    if (r0 && r0.width !== undefined) setW(r0.width);

    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r?.width != null) setW(r.width);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, w];
}

export function useViewport() {
  const [vw, setVw] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth : 0
  ));

  useEffect(() => {
    const update = () => setVw(window.innerWidth);
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return { vw };
}

export function fraudPillColors(level) {
  const l = String(level || '').toLowerCase();
  if (l === 'high') return { bg: 'var(--red-a15)', fg: 'var(--warning)' };
  if (l === 'medium') return { bg: 'var(--bot-a15)', fg: 'var(--bot)' };
  return { bg: 'var(--primary-a15)', fg: 'var(--primary)' };
}

export function Pill({ text, bg, fg }) {
  return (
    <small style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: `4px ${GAP_HALF}`,
      borderRadius: PILL_RADIUS,
      backgroundColor: bg,
      color: fg,
      whiteSpace: 'nowrap',
    }}>
      {text}
    </small>
  );
}

export function gridTemplateOverview(isMobile, vw = 0, hasDragHandle = true) {
  const small = isMobile || vw <= 980;
  if (!hasDragHandle) {
    return !small
      ? '1rem minmax(auto,3fr) 1rem minmax(2rem,4fr) minmax(1rem,3fr) minmax(1rem,2.5fr)'
      : '1rem minmax(auto,1fr) 1rem minmax(1rem,1fr) 0fr minmax(1rem,1.2fr)';
  }
  return !small
    ? '1rem 1rem minmax(auto,3fr) 1rem minmax(2rem,4fr) minmax(1rem,3fr) minmax(1rem,2.5fr)'
    : '0.5rem 1rem minmax(auto,1fr) 1rem minmax(1rem,1fr) 0fr minmax(1rem,1.2fr)';
}

export function gridTemplateSettings(isMobile, vw = 0) {
  const small = isMobile || vw <= 980;
  return !small
    ? '1rem 1rem minmax(auto,2fr) 1rem minmax(2rem,3fr) minmax(1rem,1.5fr) minmax(1rem,1fr)'
    : '0.5rem 1rem minmax(auto,1fr) 1rem minmax(1rem,1fr) minmax(1rem,1fr) minmax(1rem,1fr)';
}

export function ListHeader({ columns, cells }) {
  return (
    <div
      className="list-header"
      style={{ '--list-header-columns': columns }}
    >
      {cells.map((cell, index) => (
        <div
          key={index}
          className="list-header__cell subtle-label"
          aria-hidden={cell == null}
        >
          {cell}
        </div>
      ))}
    </div>
  );
}
